import {
  IonContent,
  IonPage,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonIcon,
  IonSpinner,
  IonText,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/react';
import type { RefresherEventDetail } from '@ionic/core';
import { informationCircleOutline } from 'ionicons/icons';
import { useIonRouter } from '@ionic/react';
import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { GET_FANTASY_LEAGUE_MATCH_WEEK_SCORES } from '../../../graphql/queries/fantasyLeagueMatchWeekScores';
import { GET_FANTASY_LEAGUE } from '../../../graphql/queries/fantasyLeague';
import type {
  GetFantasyLeagueMatchWeekScoresQuery,
  GetFantasyLeagueMatchWeekScoresQueryVariables,
  GetFantasyLeagueQuery,
  GetFantasyLeagueQueryVariables,
} from '../../../types/graphql-generated';
import { MatchWeekNavigator } from './components/MatchWeekNavigator';
import { LeagueHeader } from './components/LeagueHeader';
import { ResultsSegment } from './components/ResultsSegment';
import { StandingsSegment } from './components/StandingsSegment';
import { HelpModal } from '../components/HelpModal';
import { getPointsTabHelp } from '../../../constants/helpContent';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { LeagueSettingsButton } from '../components/LeagueSettingsButton';

interface PointsTabProps {
  leagueId: string;
}

export const PointsTab = ({ leagueId }: PointsTabProps) => {
  const { matchWeekId: urlMatchWeekId } = useParams<{ matchWeekId?: string }>();
  const ionRouter = useIonRouter();

  const [currentMatchWeekId, setCurrentMatchWeekId] = useState<string | null>(
    urlMatchWeekId || null
  );
  const [selectedSegment, setSelectedSegment] = useState<'resultados' | 'clasificacion'>(
    'resultados'
  );
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery<
    GetFantasyLeagueMatchWeekScoresQuery,
    GetFantasyLeagueMatchWeekScoresQueryVariables
  >(GET_FANTASY_LEAGUE_MATCH_WEEK_SCORES, {
    variables: {
      fantasyLeagueId: leagueId,
      matchWeekId: currentMatchWeekId,
    },
  });

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    event.detail.complete();
  };

  const { data: leagueData } = useQuery<GetFantasyLeagueQuery, GetFantasyLeagueQueryVariables>(
    GET_FANTASY_LEAGUE,
    { variables: { id: leagueId } }
  );

  const scores = data?.fantasyLeagueMatchWeekScores;
  const leagueMembers = leagueData?.fantasyLeague?.members ?? [];

  // Ribbon/sad face are always based on cumulative standings, not weekly results
  const standings = scores?.standings ?? [];
  const leadingMemberId = standings.length > 0 ? standings[0].fantasyLeagueMember.id : null;
  const lastMemberId = standings.length > 1 ? standings[standings.length - 1].fantasyLeagueMember.id : null;

  const handleNavigateToPastLineup = (fantasyLeagueMemberId: string) => {
    if (!scores?.currentMatchWeek?.id) return;

    ionRouter.push(
      `/app/ligas/${leagueId}/puntos/jornada/${scores.currentMatchWeek.id}/${fantasyLeagueMemberId}`,
      'forward'
    );
  };

  const handleNavigateToMemberTeam = (memberId: string) => {
    ionRouter.push(`/app/ligas/${leagueId}/puntos/plantilla/${memberId}`, 'forward');
  };

  return (
    <IonPage>
      <AppSectionHeader variant="fantasy">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/main/mis-ligas" />
          </IonButtons>
          <IonTitle>Puntos</IonTitle>
          <IonButtons slot="end">
            <IonButton aria-label="Ayuda" onClick={() => setIsHelpModalOpen(true)}>
              <IonIcon slot="icon-only" icon={informationCircleOutline} />
            </IonButton>
            <LeagueSettingsButton leagueId={leagueId} />
          </IonButtons>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <LeagueHeader leagueId={leagueId} />

        {loading && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && (
          <IonText color="danger">
            <p>Error al cargar los puntos</p>
          </IonText>
        )}

        {!loading && !error && scores && (
          <MatchWeekNavigator
            currentWeekNumber={scores.currentMatchWeek.weekNumber}
            hasPrevious={scores.hasPrevious}
            hasNext={scores.hasNext}
            onPrevious={() => setCurrentMatchWeekId(scores.previousMatchWeekId || null)}
            onNext={() => setCurrentMatchWeekId(scores.nextMatchWeekId || null)}
          />
        )}

        <IonSegment
          value={selectedSegment}
          onIonChange={(e) =>
            setSelectedSegment(e.detail.value as 'resultados' | 'clasificacion')
          }
          className="mb-4"
        >
          <IonSegmentButton value="resultados">
            <IonLabel>Resultados</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="clasificacion">
            <IonLabel>Clasificación</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        {!loading && !error && (
          <>
            {selectedSegment === 'resultados' && (
              <ResultsSegment
                lineups={scores?.lineups ?? []}
                leadingMemberId={leadingMemberId}
                lastMemberId={lastMemberId}
                onLineupClick={handleNavigateToPastLineup}
              />
            )}
            {selectedSegment === 'clasificacion' && (
              <StandingsSegment
                standings={scores?.standings ?? []}
                previousStandings={scores?.previousStandings ?? []}
                leadingMemberId={leadingMemberId}
                lastMemberId={lastMemberId}
                members={leagueMembers}
                onMemberClick={handleNavigateToMemberTeam}
              />
            )}
          </>
        )}
      </IonContent>

      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        title={getPointsTabHelp().title}
        content={getPointsTabHelp().content}
      />
    </IonPage>
  );
};
